import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MyCollectionsTab } from '../components/MyCollectionsTab';
import { BrowseCollectionsTab } from '../components/BrowseCollectionsTab';
import { ProblemsTab } from './ProblemsTab';
import { useAuth } from '../contexts/FirebaseAuthContext';
import '../styles/HomePage.css';

type TabType = 'my-collections' | 'browse' | 'problems';

export const HomePage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial tab from URL query parameter, or default based on auth status
  const getInitialTab = (): TabType => {
    const tabParam = searchParams.get('tab') as TabType | null;
    if (tabParam && ['my-collections', 'browse', 'problems'].includes(tabParam)) {
      return tabParam;
    }
    return isAuthenticated ? 'my-collections' : 'browse';
  };

  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab());

  // Update URL and page title when active tab changes
  useEffect(() => {
    // Update URL query parameter
    setSearchParams({ tab: activeTab }, { replace: true });

    // Update page title
    const titles = {
      'my-collections': 'My Collections - AIPrep',
      'browse': 'Popular Collections - AIPrep',
      'problems': 'Problems - AIPrep'
    };
    document.title = titles[activeTab];
  }, [activeTab, setSearchParams]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'my-collections':
        return <MyCollectionsTab onTabChange={setActiveTab} />;
      case 'browse':
        return <BrowseCollectionsTab />;
      case 'problems':
        return <ProblemsTab />;
      default:
        return <MyCollectionsTab onTabChange={setActiveTab} />;
    }
  };

  return (
    <div className="home-page">
      <div className="tabs-container">
        <div className="tabs">
          {isAuthenticated ? (
            // When signed in: My Collections, Popular Collections, Problems
            <>
              <button
                className={`tab ${activeTab === 'my-collections' ? 'active' : ''}`}
                onClick={() => setActiveTab('my-collections')}
              >
                My Collections
              </button>
              <button
                className={`tab ${activeTab === 'browse' ? 'active' : ''}`}
                onClick={() => setActiveTab('browse')}
              >
                Popular Collections
              </button>
            </>
          ) : (
            // When not signed in: Popular Collections, My Collections, Problems
            <>
              <button
                className={`tab ${activeTab === 'browse' ? 'active' : ''}`}
                onClick={() => setActiveTab('browse')}
              >
                Popular Collections
              </button>
              <button
                className={`tab ${activeTab === 'my-collections' ? 'active' : ''}`}
                onClick={() => setActiveTab('my-collections')}
              >
                My Collections
              </button>
            </>
          )}
          <button
            className={`tab ${activeTab === 'problems' ? 'active' : ''}`}
            onClick={() => setActiveTab('problems')}
          >
            Problems
          </button>
        </div>
      </div>
      
      <div className="tab-content-container">
        <div className="tab-content">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};